import { useState } from 'react';
import { ScheduleException } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/exception.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, formatDate, Heading, useModalControl } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../../constants';
import { AddEditRuleExceptionsTray } from './components/add-edit-rule-exceptions-tray';
import { DeleteConfirmationModal } from './components/delete-confirmation-modal';
import { SortFilterValue } from './components/sort-filter';
import { useRuleExceptionsFilters } from './hooks/use-rule-exceptions-filters';
import { RuleExceptionsTable } from './rule-exceptions-table';

type Props = {
  locationId: string;
};

export const RuleExceptions = ({ locationId }: Props) => {
  const { t } = useTranslation('schedule-settings');
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState<SortFilterValue>('NEXT_OCCURRENCE');
  const [selectedException, setSelectedException] = useState<ScheduleException | null>(null);

  const { modalProps: editModalProps, triggerProps: editTriggerProps } = useModalControl();
  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();

  const { filters, quickFilters, handleFiltersChange, handleQuickFiltersChange } = useRuleExceptionsFilters(locationId);

  const { data, isFetching } = SchedulerV3.Queries.useListScheduleExceptionsQuery({
    request: {
      locationId,
      ...(searchValue.length > 1 && { name: searchValue }),
      ...(filters.providers?.length > 0 && { providers: filters.providers }),
      ...(filters.operatories?.length > 0 && { operatories: filters.operatories }),
      ...(filters.serviceTypes?.length > 0 && { serviceTypes: filters.serviceTypes }),
      ...(filters.startDate && { timeFrameStart: formatDate(filters.startDate, DATETIME_FORMATS.ISO_DATE) }),
      ...(filters.endDate && { timeFrameEnd: formatDate(filters.endDate, DATETIME_FORMATS.ISO_DATE) }),
      ...(filters.view && { view: filters.view }),
      ...(filters.recurrenceTypes?.length && { recurrenceTypes: filters.recurrenceTypes }),
      ...(sortBy && { sortBy, sortOrder: sortBy === 'NEXT_OCCURRENCE' ? 'DESC' : 'ASC' }),
    },
  });

  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Heading level={2}>{t('Rule Exceptions')}</Heading>
        <Button {...editTriggerProps}>{t('+ Add Exception')}</Button>
      </div>
      <RuleExceptionsTable
        data={data?.exceptions || []}
        isLoading={isFetching}
        locationId={locationId}
        editTriggerProps={editTriggerProps}
        deleteTriggerProps={deleteTriggerProps}
        setSelectedException={setSelectedException}
        searchValue={searchValue}
        searchHandler={setSearchValue}
        filters={filters}
        quickFilters={quickFilters}
        setFilters={handleFiltersChange}
        setQuickFilters={handleQuickFiltersChange}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
      {editModalProps.show && (
        <AddEditRuleExceptionsTray
          locationId={locationId}
          selectedException={selectedException}
          modalProps={editModalProps}
          onClose={() => {
            setSelectedException(null);
            editModalProps.onClose();
          }}
        />
      )}
      {deleteModalProps.show && (
        <DeleteConfirmationModal
          locationId={locationId}
          selectedException={selectedException}
          modalProps={deleteModalProps}
          onClose={() => {
            setSelectedException(null);
            deleteModalProps.onClose();
          }}
        />
      )}
    </div>
  );
};
